import { useEffect, useMemo, useRef, useState } from "react";

import { api, ApiError } from "../api/client";
import { downloadJson, readFileText } from "../lib/download";
import { useFilters } from "../lib/filters";
import { hasRack } from "../lib/planogram";
import { urlParam, useUrlState } from "../lib/urlState";
import { useAsync } from "../lib/useAsync";
import { useTransientStatus } from "../lib/useTransientStatus";
import { ui } from "../lib/ui";
import type {
  Branch,
  BranchSetup,
  CameraConfig,
  Fixture,
  Planogram,
  Product,
} from "../types";
import { CameraFootageField } from "./CameraFootageField";
import { LinePicker } from "./LinePicker";
import { Panel } from "./Panel";
import { PlanogramEditor } from "./PlanogramEditor";
import { ShelfEditor } from "./ShelfEditor";

/** Loads, edits and saves one branch's floor plan (PUT wholesale), and hosts
 *  the per-camera processing setup: select a camera on the plan, click
 *  "Set up…". A store may have several entrances — every marker flagged as an
 *  entrance gets its own full setup (entry line, sampling, confidence), saved
 *  via PUT /branches/{id}/cameras/{marker id}; other cameras only need their
 *  footage. Every camera may be linked to the folder its recordings already
 *  live in (`CameraFootageField`); otherwise its files are copied into a
 *  folder named after the marker id under each day's footage folder.
 *
 *  It also switches between the plan and the shelf designer of one fixture
 *  (`rack=<fixture id>` in the URL): the rack design is part of the fixture,
 *  so it rides along in the same draft and the same save. */

/** Distinct products placed anywhere in the plan (by name), so a product
 *  designed once can be dropped onto another rack with one click. */
function productCatalogue(fixtures: Fixture[]): Product[] {
  const seen = new Set<string>();
  const out: Product[] = [];
  for (const f of fixtures) {
    for (const s of f.shelves) {
      for (const p of s.products) {
        const key = p.name.trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(p);
      }
    }
  }
  return out;
}

const BLANK_PLAN: Planogram = {
  width: 12,
  height: 8,
  fixtures: [],
  walls: [],
  cameras: [],
};

const BLANK_CAMERA: CameraConfig = {
  fps_sample: 3,
  detection_confidence: 0.4,
  entry_line: { start: [0, 0], end: [0, 0], inward: "down" },
  frame_size: null,
};

const cloneCamera = (camera: CameraConfig | null): CameraConfig =>
  camera
    ? { ...camera, entry_line: { ...camera.entry_line } }
    : { ...BLANK_CAMERA, entry_line: { ...BLANK_CAMERA.entry_line } };

export function PlanogramPanel({
  branch,
  onClose,
  onEditLocation,
}: {
  branch: Branch;
  onClose: () => void;
  /** Open the branch's location form (name, address, …) — the plan panel is
   *  the single per-branch entry point, so location editing hangs off it. */
  onEditLocation?: () => void;
}) {
  const { branches, reloadBranches } = useFilters();
  // The branch list refreshes after a camera-config save; prefer the fresh row.
  const current = branches.find((b) => b.id === branch.id) ?? branch;

  const { data, error, loading, reload } = useAsync(
    () => api.getPlanogram(branch.id),
    [branch.id],
  );
  const [draft, setDraft] = useState<Planogram | null>(null);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [status, setStatus] = useTransientStatus();
  const [saveError, setSaveError] = useState<string | null>(null);

  // Per-camera setup: which marker is open, and the branch camera-config draft.
  const [setupId, setSetupId] = useState<string | null>(null);
  const [cameraDraft, setCameraDraft] = useState<CameraConfig | null>(null);
  const [cameraSaving, setCameraSaving] = useState(false);

  // The notices sit at the top of the panel; the buttons that trigger them can
  // be a full plan below the fold, so bring the feedback to the viewport.
  const noticeRef = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    if (status || saveError)
      noticeRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [status, saveError]);

  // Which fixture's shelf designer is open (URL: `rack`), and the last one
  // closed so the plan comes back with that fixture selected.
  const [rackId, setRackId] = useState<string | null>(() => urlParam("rack"));
  const lastRackId = useRef<string | null>(null);
  useUrlState({ rack: rackId ?? "" }, (p) => {
    if ("rack" in p) setRackId(p.rack || null);
  });

  // (Re)seed the draft whenever a load finishes: the stored plan, or a blank
  // outline for a branch that has none yet.
  useEffect(() => {
    if (loading) return;
    setDraft(
      data
        ? {
            width: data.width,
            height: data.height,
            fixtures: data.fixtures,
            walls: data.walls ?? [],
            cameras: data.cameras,
          }
        : { ...BLANK_PLAN },
    );
    setSavedAt(data?.updated_at ?? null);
    setDirty(false);
    setSetupId(null);
  }, [loading, data]);

  const edit = (plan: Planogram) => {
    setDraft(plan);
    setDirty(true);
  };

  const discard = () => {
    setDraft(
      data
        ? {
            width: data.width,
            height: data.height,
            fixtures: data.fixtures,
            walls: data.walls ?? [],
            cameras: data.cameras,
          }
        : { ...BLANK_PLAN },
    );
    setDirty(false);
  };

  const openRack = (fixtureId: string) => {
    setSetupId(null);
    setRackId(fixtureId);
  };

  const closeRack = () => {
    lastRackId.current = rackId;
    setRackId(null);
  };

  const rackFixture =
    (rackId && draft?.fixtures.find((f) => f.id === rackId && hasRack(f))) || null;
  const catalogue = useMemo(
    () => (draft ? productCatalogue(draft.fixtures) : []),
    [draft],
  );
  const editFixture = (fixture: Fixture) => {
    if (!draft) return;
    edit({
      ...draft,
      fixtures: draft.fixtures.map((f) => (f.id === fixture.id ? fixture : f)),
    });
  };

  const save = async () => {
    if (!draft) return;
    setSaveError(null);
    try {
      const saved = await api.putPlanogram(branch.id, draft);
      setSavedAt(saved.updated_at);
      setDirty(false);
      setStatus("Floor plan saved");
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : String(err));
    }
  };

  // Export / import of the whole setup (plan + entrance cameras) as one JSON
  // file, so a store drawn on one PC can be applied on the branch PC. The
  // file is read locally; only the parsed document goes to the API.
  const importInput = useRef<HTMLInputElement | null>(null);

  const exportSetup = async () => {
    setSaveError(null);
    try {
      const setup = await api.getBranchSetup(branch.id);
      downloadJson(`${branch.id}-setup.json`, setup);
      setStatus(
        `Exported ${Object.keys(setup.cameras).length} camera setup(s)` +
          `${setup.planogram ? " and the floor plan" : " (no floor plan yet)"}`,
      );
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const importSetup = async (file: File) => {
    setSaveError(null);
    let setup: BranchSetup;
    try {
      setup = JSON.parse(await readFileText(file)) as BranchSetup;
    } catch (err) {
      setSaveError(`${file.name} is not valid JSON: ${String(err)}`);
      return;
    }
    if (setup?.format !== "footfall-branch-setup") {
      setSaveError(`${file.name} is not a branch setup export`);
      return;
    }
    const cameraCount = Object.keys(setup.cameras ?? {}).length;
    const mismatch = setup.branch_id !== branch.id;
    const confirmed = window.confirm(
      `Import "${file.name}" into ${branch.name || branch.id}?\n\n` +
        `It replaces this branch's floor plan${setup.planogram ? "" : " (removing it)"} ` +
        `and its entrance cameras (${cameraCount} in the file). Footfall data, tracks ` +
        "and staff enrollments are not touched." +
        (mismatch
          ? `\n\nNote: the file was exported from a different branch ` +
            `(${setup.branch_name || setup.branch_id}).`
          : "") +
        (dirty ? "\n\nUnsaved changes to the plan are discarded." : ""),
    );
    if (!confirmed) return;
    try {
      await api.putBranchSetup(branch.id, setup, mismatch);
      setStatus(`Imported ${cameraCount} camera setup(s) and the floor plan from ${file.name}`);
      reload();
      reloadBranches();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const removePlan = async () => {
    const confirmed = window.confirm(
      `Delete the floor plan of "${branch.name}"? The branch itself and its ` +
        "footfall data are not touched.",
    );
    if (!confirmed) return;
    setSaveError(null);
    try {
      await api.deletePlanogram(branch.id);
      setStatus("Floor plan deleted");
      reload();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const openSetup = (cameraId: string) => {
    setSetupId(cameraId);
    setCameraDraft(cloneCamera(current.cameras[cameraId] ?? null));
  };

  const setupCamera = draft?.cameras.find((c) => c.id === setupId) ?? null;
  // A marker is an entrance when flagged on the plan or already configured —
  // the saved config is what the pipeline actually counts with.
  const setupIsEntrance =
    setupCamera !== null &&
    (setupCamera.entry || setupCamera.id in current.cameras);

  // Configured entrance cameras whose marker no longer exists on the plan —
  // their footage folders still get counted, so surface the drift.
  const orphanedCameraIds = Object.keys(current.cameras).filter(
    (id) => !draft?.cameras.some((c) => c.id === id),
  );

  const makeEntry = (cameraId: string) => {
    if (!draft) return;
    edit({
      ...draft,
      cameras: draft.cameras.map((c) =>
        c.id === cameraId ? { ...c, entry: true } : c,
      ),
    });
  };

  const lineDrawn =
    cameraDraft !== null &&
    (cameraDraft.entry_line.start[0] !== cameraDraft.entry_line.end[0] ||
      cameraDraft.entry_line.start[1] !== cameraDraft.entry_line.end[1]);
  // A line outside the frame it is bound to can never be crossed (the API
  // rejects it too); this only arises mid-redraw of a legacy line.
  const lineFits =
    cameraDraft === null ||
    cameraDraft.frame_size === null ||
    [cameraDraft.entry_line.start, cameraDraft.entry_line.end].every(
      (p) =>
        p[0] <= cameraDraft.frame_size![0] && p[1] <= cameraDraft.frame_size![1],
    );

  const saveCameraConfig = async () => {
    if (!cameraDraft || !setupCamera || cameraSaving) return;
    setSaveError(null);
    setCameraSaving(true);
    try {
      await api.putBranchCamera(branch.id, setupCamera.id, cameraDraft);
      reloadBranches();
      setStatus(
        `Entrance camera "${setupCamera.label || setupCamera.id}" saved — its ` +
          "footage is now counted",
      );
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setCameraSaving(false);
    }
  };

  const removeEntranceCamera = async (cameraId: string) => {
    const confirmed = window.confirm(
      `Stop counting entrance camera "${cameraId}" of "${branch.name}"? Its ` +
        "entry line and sampling setup are removed; recorded aggregates are " +
        "kept. Its footage folder is then processed as occupancy-only.",
    );
    if (!confirmed) return;
    setSaveError(null);
    try {
      await api.deleteBranchCamera(branch.id, cameraId);
      reloadBranches();
      setCameraDraft(cloneCamera(null));
      setStatus(`Entrance camera "${cameraId}" removed`);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <Panel
      title={
        rackFixture
          ? `Shelf design — ${rackFixture.label || rackFixture.kind} · ${branch.name || branch.id}`
          : `Floor plan — ${branch.name || branch.id}`
      }
      subtitle={
        rackFixture
          ? "Shelf heights and product placement, at real scale · part of the floor plan — save it to keep the design"
          : savedAt
            ? `Fixtures, walls and camera positions · saved ${new Date(savedAt).toLocaleString()} · select a camera and “Set up…” to configure processing`
            : "No floor plan yet — draw the store, then place cameras and set them up"
      }
      actions={
        <div className="flex gap-1.5">
          <button
            type="button"
            className={ui.btnQuiet}
            onClick={exportSetup}
            title="Download this branch's floor plan and entrance-camera setup as a JSON file, to import on another PC"
          >
            Export setup
          </button>
          <button
            type="button"
            className={ui.btnQuiet}
            onClick={() => importInput.current?.click()}
            title="Replace this branch's floor plan and entrance cameras with an exported setup file"
          >
            Import setup…
          </button>
          <input
            ref={importInput}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = ""; // the same file can be picked again
              if (file) void importSetup(file);
            }}
          />
          {onEditLocation && (
            <button type="button" className={ui.btnQuiet} onClick={onEditLocation}>
              Edit location
            </button>
          )}
          <button type="button" className={ui.btnQuiet} onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      {status && (
        <p ref={noticeRef} className={`${ui.notice} ${ui.noticeSuccess}`}>
          {status}
        </p>
      )}
      {saveError && (
        <p ref={status ? undefined : noticeRef} className={`${ui.notice} ${ui.noticeError}`}>
          {saveError}
        </p>
      )}

      {error ? (
        <p className={`${ui.notice} ${ui.noticeError}`}>{error}</p>
      ) : loading || !draft ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <>
          {rackFixture ? (
            <ShelfEditor
              key={rackFixture.id}
              fixture={rackFixture}
              onChange={editFixture}
              onBack={closeRack}
              catalogue={catalogue}
            />
          ) : (
            <PlanogramEditor
              value={draft}
              onChange={edit}
              onSetup={openSetup}
              onDesign={openRack}
              initialFixtureId={lastRackId.current}
            />
          )}
          <div className="mt-4 flex items-center gap-2">
            <button type="button" className={ui.btn} onClick={save} disabled={!dirty}>
              Save floor plan
            </button>
            {dirty && (
              <button type="button" className={ui.btnQuiet} onClick={discard}>
                Discard changes
              </button>
            )}
            {savedAt && !rackFixture && (
              <button type="button" className={ui.btnDanger} onClick={removePlan}>
                Delete floor plan
              </button>
            )}
            {dirty && <span className="text-xs text-muted">Unsaved changes</span>}
          </div>

          {orphanedCameraIds.length > 0 && !rackFixture && (
            <p className={`${ui.notice} mt-4`}>
              Entrance camera setup exists for{" "}
              <span className="font-mono text-xs">
                {orphanedCameraIds.join(", ")}
              </span>{" "}
              but the plan has no marker with that id — footage in those
              folders is still counted. Re-add the marker, or remove the setup
              from its camera panel.
            </p>
          )}

          {setupCamera && !rackFixture && (
            <div className="mt-5 rounded-card border border-border bg-surface px-4 py-4">
              <div className="mb-3 flex items-start justify-between gap-4">
                <div>
                  <h3 className="m-0 text-[15px] font-semibold">
                    Camera setup — {setupCamera.label || setupCamera.id}
                  </h3>
                  <p className="mt-1 text-[13px] text-muted">
                    {setupIsEntrance
                      ? "An entrance camera: its footage is counted with its own entry line below. A store with several doors gets one entrance camera per door."
                      : "An occupancy camera: its footage is detection-only for the heatmap."}
                  </p>
                </div>
                <button
                  type="button"
                  className={ui.btnQuiet}
                  onClick={() => setSetupId(null)}
                >
                  Close setup
                </button>
              </div>

              <CameraFootageField
                key={`${branch.id}:${setupCamera.id}`}
                branch={current}
                cameraId={setupCamera.id}
                onSaved={(message) => {
                  setSaveError(null);
                  reloadBranches();
                  setStatus(message);
                }}
                onError={setSaveError}
              />

              {!setupIsEntrance ? (
                <div className="flex flex-col gap-3">
                  <p className="text-[13px]">
                    Each run records this camera's hourly occupancy, visits and
                    dwell for the heatmap — nothing else to configure.
                  </p>
                  <div>
                    <button
                      type="button"
                      className={ui.btnQuiet}
                      onClick={() => makeEntry(setupCamera.id)}
                    >
                      Make this an entrance camera
                    </button>
                  </div>
                </div>
              ) : cameraDraft ? (
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3.5">
                    <label className={ui.field}>
                      Sampled frames per second
                      <input
                        className={ui.input}
                        type="number"
                        min={1}
                        max={30}
                        value={cameraDraft.fps_sample}
                        onChange={(e) =>
                          setCameraDraft({
                            ...cameraDraft,
                            fps_sample: Number(e.target.value),
                          })
                        }
                      />
                    </label>
                    <label className={ui.field}>
                      Detection confidence
                      <input
                        className={ui.input}
                        type="number"
                        step={0.05}
                        min={0.05}
                        max={0.95}
                        value={cameraDraft.detection_confidence}
                        onChange={(e) =>
                          setCameraDraft({
                            ...cameraDraft,
                            detection_confidence: Number(e.target.value),
                          })
                        }
                      />
                    </label>
                  </div>

                  <div>
                    <h4 className="mb-2 mt-1 text-[13px] font-semibold uppercase tracking-[0.04em] text-muted">
                      Entry line
                    </h4>
                    <LinePicker
                      key={`${branch.id}:${setupCamera.id}`}
                      value={cameraDraft.entry_line}
                      frameSize={cameraDraft.frame_size}
                      onChange={(entry_line, frame_size) =>
                        setCameraDraft((prev) =>
                          prev ? { ...prev, entry_line, frame_size } : prev,
                        )
                      }
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className={ui.btn}
                      onClick={saveCameraConfig}
                      disabled={!lineDrawn || !lineFits || cameraSaving}
                    >
                      {cameraSaving ? "Saving…" : "Save camera setup"}
                    </button>
                    {!lineDrawn ? (
                      <span className="text-xs text-muted">
                        Draw the entry line on a sample clip first.
                      </span>
                    ) : !lineFits ? (
                      <span className="text-xs text-muted">
                        Finish redrawing the line inside the sample.
                      </span>
                    ) : cameraDraft.frame_size ? (
                      <span className="text-xs text-muted">
                        Footage is processed at {cameraDraft.frame_size[0]}×
                        {cameraDraft.frame_size[1]}.
                      </span>
                    ) : (
                      <span className="text-xs text-muted">
                        No frame size recorded — footage is processed at its
                        native resolution. Open a sample to record one.
                      </span>
                    )}
                    {setupCamera.id in current.cameras && (
                      <button
                        type="button"
                        className={ui.btnDanger}
                        onClick={() => removeEntranceCamera(setupCamera.id)}
                      >
                        Remove entrance camera
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-muted">
                    Retuning the entry line changes future counts only —
                    re-queue past days from the Runs page to recount them.
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
